import { Response } from 'app/services/http';
import * as jsonpath from '../../../../External/jsonpath';

import { parseJsonPath as shellParseJsonPath, capitalize as shellCapitalize, format, stringsAreEqualCaseInsensitive as shellStringsAreEqualCaseInsensitive } from 'shell-models-lib';
import * as path from 'path';
import _ from 'lodash';

export { format, formatWithEscape, sanitizeAndFormatString, getConnectorLegacyNameByFlag } from 'shell-models-lib';

export function formatWithDict(template: string, dict: { [key: string]: string }): string {
    return template.replace(/\{([\d\w]+)\}/g, (match, key) => {
        return dict[key] || '';
    });
}

export function getString(value?: string): string {
    if (value) {
        return value;
    }
    throw new Error('Passed string is not valid');
}

export function getStringOrDefaultValue(defaultValue: string, value?: string): string {
    if (value) {
        return value;
    }
    return defaultValue;
}

export function convertStringToNumber(value: string, dataType: 'integer' | 'float' = 'integer'): number | undefined {
    const result = dataType === 'integer' ? parseInt(value, 10) : parseFloat(value);
    return isNaN(result) ? undefined : result;
}

export function stringsAreEqualCaseInsensitive(string1?: string, string2?: string): boolean {
    return shellStringsAreEqualCaseInsensitive(string1, string2);
}

export function stringIsNullOrEmpty(value?: string): boolean {
    return value == null || value === '';
}

export function stringIsNullOrWhitespace(value?: string): boolean {
    return value == null || value.trim() === '';
}

export function string2UnicodeSingle(value: string): string {
    const regex = new RegExp(/^\\u([0-9a-f]){4}$/);
    return regex.test(value) ? String.fromCharCode(parseInt(value.substr(2), 16)) : value;
}

export function unicode2StrSingle(value: string, skipSymbols: string[] = [], skipPrintable: boolean = true): string {
    if (!value || value.length !== 1 || skipSymbols.indexOf(value) > -1) {
        return value;
    } else {
        if (skipPrintable) {
            const printableRegex = new RegExp(/^[\u0020-\u007e\u00a0-\u00ff]$/);
            if (printableRegex.test(value)) {
                return value;
            }
        }
        return '\\u' + ('0000' + value.codePointAt(0).toString(16)).substr(-4);
    }
}

export function containWilcard(path: string): boolean {
    return /.*[\*\?].*/.test(path);
}

export function splitWithEscapeChar(value: string, splitChar: string, escapeChar: string): string[] {
    const list: string[] = [];
    let buffer = '';
    let isEscape = false;
    for (const c of value) {
        if (!isEscape && c === splitChar) {
            list.push(buffer);
            buffer = '';
            continue;
        }
        if (c === escapeChar) {
            isEscape = !isEscape;
            if (isEscape) {
                continue;
            }
        } else {
            isEscape = false;
        }
        buffer += c;
    }
    if (buffer.length > 0) {
        list.push(buffer);
    }
    return list;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertObjectToString(message: any, keepStack: boolean = true): string {
    if (typeof message !== 'string' && message != null) {
        let messageStr: string;
        if (message instanceof Response) {
            messageStr = message.text();
        } else if (!keepStack && message instanceof Error) {
            // If it's Error, and don't want to keep the stack, use its message
            // By default, keepStack is true, it's used to log telemetry data
            // For components which shows error message to customer, keepStack should be false
            messageStr = message.message;
        } else {
            try {
                messageStr = (message.error) ? JSON.stringify(message.error, Object.getOwnPropertyNames(message.error)) : JSON.stringify(message, Object.getOwnPropertyNames(message));
            } catch {
                try {
                    messageStr = JSON.stringify(message);
                } catch {
                    messageStr = '' + message;
                }
            }
        }
        return messageStr;
    }
    return message;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertStringToObject(objectString: string): any {
    let parsedObject;
    try {
        parsedObject = JSON.parse(objectString);
    } catch (error) {
        parsedObject = undefined;
    }
    return parsedObject;
}

export function parseKeyValueStringToObject(value: string): Record<string, string> {
    // Example: "key1: value1,\nkey2: value2"
    // Convert to object: { key1: value1, key2: value2 }
    const result: Record<string, string> = {};
    if (value) {
       return value.split(',\n').reduce((acc, line) => {
            const [key, value] = line.split(':').map((part) => part.trim());
            if (key && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, result); 
    }
    return result;
}

export function convertStringToBool(value: string): boolean {
    let convertedValue: boolean;

    if (value) {
        if (stringsAreEqualCaseInsensitive(value, 'true')) {
            convertedValue = true;
        } else if (stringsAreEqualCaseInsensitive(value, 'false')) {
            convertedValue = false;
        }
    }

    return convertedValue;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isString(obj: any): boolean {
    return obj != null && typeof obj === 'string';
}

export function formatStringForCompare(str: string): string {
    return str?.replace(/\s/g, '').toLowerCase();
}

export function startsWithVowel(str: string): boolean {
    return /\b[aeiou]\w*/ig.test(str);
}

export function hasWildcardChar(str: string): boolean {
    let index = 0;
    while (index < str.length) {
        if (str[index] === '^') {
            index += 2;
            continue;
        }
        if (str[index] === '*' || str[index] === '?') {
            return true;
        }
        ++index;
    }
    return false;
}

export function cleanseFileName(str: string): string {
    if (!str) {
        return str;
    }

    const parts = str.split('.');
    if (parts.length > 1) {
        parts.splice(parts.length - 1, 1);
        return parts.join('');
    }
    return parts[0];
}

export function joinPaths(...paths: string[]): string {
    return path.join(...paths);
}

export function normalizePath(filePath: string): string {
    if (process && process.platform === 'win32') {
        return path.normalize(filePath.replace(/\//g, '\\'));
    }
    return path.normalize(filePath.replace(/\\/g, '/'));
}

export function getEnvPathSlash(): string {
    if (process && process.platform === 'win32') {
        return '\\';
    }
    return '/';
}

/*
 * If string is null or undefined, return empty string ''
 * If string starts with an alphabet character, capitalize it and return the string
 * If string starts with a none alphabet character, return it as it
 */
export function capitalize(text: string): string {
    return shellCapitalize(text);
}

export function containNonASCII(value: string): boolean {
    for (let i = 0; i < value.length; i++) {
        if (value.charCodeAt(i) > 127) {
            return true;
        }
    }
    return false;
}

export function stringContains(a: string, b: string): boolean {
    if ((a != null) && (b != null)) {
        return a.toLowerCase().indexOf(b.toLowerCase()) >= 0;
    }
    return false;
}

/**
 * Returns the number of leading whitespace characters in the argument.
 * @param value string to check for leading whitespace.
 */
export function numberOfLeadingWhitespace(value: string): number {
    return (value) ? (value.length - value.trimLeft().length) : 0;
}

export function parseJsonPath(path: string): jsonpath.PathComponent[] {
    return shellParseJsonPath(path);
}

/**
 * If the JSON path contains query, wildcard, returns true
 * @param path JSON path to be parsed
 */
export function isComplexJsonPath(path: string, includingArray?: boolean): boolean {
    const keyWord = ['filter_expression', 'wildcard', 'script_expression', 'union', 'slice'];
    if (includingArray) {
        keyWord.push('numeric_literal');
    }
    try {
        const pathItem = jsonpath.parse(path);
        return pathItem.find((item) => keyWord.indexOf(item.expression.type) >= 0) != null;
    } catch {
        return false;
    }
}

export function trimLeft(str: string, charsToTrim: string): string {
    return str.replace(new RegExp(`^[${charsToTrim}]+`), '');
}

export function trimRight(str: string, charsToTrim: string): string {
    return str.replace(new RegExp(`[${charsToTrim}]+$`), '');
}

export function trim(str: string, charsToTrim: string): string {
    return trimLeft(trimRight(str, charsToTrim), charsToTrim);
}

/**
 * Convert string resources based on the application type (e.g. Azure Data Factory)
 * @param resource String resource to format.
 * @param formatArgs Rest of the argument to pass to the format function (application
 *                   type is the first argument to format)
 */
export function formatStringForAppTypeFullName(resource: string, ...formatArgs: string[]): string {
    return format(resource, __IS_A365__ ? stringResources.AzureSynapseAnalytics : stringResources.AzureDataFactory, ...formatArgs);
}

/**
 * Convert string resources based on the application type (e.g. Data Factory)
 * @param resource String resource to format.
 * @param formatArgs Rest of the argument to pass to the format function (application
 *                   type is the first argument to format)
 */
export function formatStringForAppType(resource: string, ...formatArgs: string[]): string {
    return format(resource, __IS_A365__ ? stringResources.WorkspaceLowerCase : stringResources.DataFactory, ...formatArgs);
}

/**
 * Convert string resources based on the application type (e.g. Synapse workspace, Data Factory)
 * @param resource String resource to format.
 * @param formatArgs Rest of the argument to pass to the format function (application
 *                   type is the first argument to format)
 */
export function formatStringForAppTypeLongName(resource: string, ...formatArgs: string[]): string {
    return format(resource, __IS_A365__ ? stringResources.SynapseWorkspace : stringResources.DataFactory, ...formatArgs);
}

/**
 * Convert string resources based on the application type.
 * The application type string is in plural form (e.g. Data Factories)
 * @param resource String resource to format.
 * @param formatArgs Rest of the argument to pass to the format function (application
 *                   type is the first argument to format)
 */
export function formatStringForAppTypePlural(resource: string, ...formatArgs: string[]): string {
    return format(resource, __IS_A365__ ? stringResources.WorkspacesLowerCase : stringResources.Factories, ...formatArgs);
}

/**
 * Convert string resources based on the application type.
 * The application type string is in lower case form (e.g. workspace or factory)
 * @param resource String resource to format.
 * @param formatArgs Rest of the argument to pass to the format function (application
 *                   type is the first argument to format)
 */
export function formatStringForAppTypeLower(resource: string, ...formatArgs: string[]): string {
    return format(resource, __IS_A365__ || __IS_FABRIC_DPE__ ? stringResources.WorkspaceLowerCase : stringResources.FactoryLowerCase, ...formatArgs);
}

export function formatStringForAppTypeUpper(resource: string, ...formatArgs: string[]): string {
    return format(resource, __IS_A365__ || __IS_FABRIC_DPE__ ? stringResources.Workspace : stringResources.Factory, ...formatArgs);
}

export function formatStringForDestination(resource: string, ...formatArgs: string[]): string {
    return format(resource, __IS_FABRIC_DPE__ ? stringResources.DestinationLabel.toLocaleLowerCase() : stringResources.Sink.toLocaleLowerCase(), ...formatArgs);
}

/**
 * Removes special characters for inputting a user string into regex
 * @param input String to Escape
 */
export function escapeRegExp(input: string): string {
    return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Removes special characters from auto generated datasets in scope script compilation
 * @param input String to Escape
 */
export function replaceSpecialChars(input: string): string {
    return input.replace(/[.*+?^${}()%|[\]\\]/g, '');
}

/**
 * Removes special characters when powerquerysource object is auto added on dataset selection
 * @param input String to Escape
 */
export function replaceSpecialCharsAndUnderscore(input: string): string {
    return input.replace(/[_.*+?^${}()%|[\]\\]/g, '');
}

export type ResourceIdParam = 'subscriptions' | 'resourceGroups' | 'accounts' | 'storageAccounts';

export function extractResourceIdParamValue(paramKey: ResourceIdParam, resourceId: string): string {
    const paramKeyMatches: RegExpExecArray | undefined[] = new RegExp(`.*${paramKey}\/(.*?)\/.*`).exec(resourceId + '/') || [];

    return paramKeyMatches.length > 1 ? paramKeyMatches[1] : '';
}

export const matchInQuotes = /(["'])(?:(?=(\\?))\2.)*?\1/g;

/**
 * Build encoded URL using path name and searches
 * Empty search value will be abandoned
 */
export function buildUrl(pathName: string, searches?: { [key: string]: string }) {
    if (_.isEmpty(searches)) {
        return encodeURI(pathName);
    }
    let url = pathName + '?';
    Object.entries(searches).forEach(([key, value]) => {
        if (value == null || value === '') {
            return;
        }
        url += `&${key}=${value}`;
    });
    return encodeURI(url);
}

const getCircularReplacer = () => {
    const seen = new WeakSet();
    return (key: string | number, value: never) => {
        if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
                return;
            }
            seen.add(value);
        }
        return value;
    };
};

export function stringifyCircularReference(circularReference: unknown): string {
    return JSON.stringify(circularReference, getCircularReplacer());
}

/**
 * Encoding Unicode -> base64
 *  for instance:
 *  Everytime we need encode non-single byte unicode like: U+2713 "✓": 0010 0111 0001 0011
 *  encodeURIComponent would first encode it into a 3-byte utf8
 *          1110 (0010)     which is 0xE2
 *          10(01 1100)     which is 0x9C
 *          10(01 0011)     which is 0x93
 *  and escape the three bytes into %hexadecimal like:
 *          %E2%9C%93
 *  then regex /%([0-9A-F]{2})/g pick the hexadecimal up and save it into unsigned char/byte
 *          0xE2 0x9C 0x93  which is literally: 'â\x9C\x93'
 *  and then btoa could always save the single byte without worrying the InvalidCharacterError
 *  https://developer.mozilla.org/en-US/docs/Web/API/btoa#exceptions
 *          '4pyT'
 * @param str
 */
export function b64EncodeUtf8(str: string): string {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function(match, p1) {
        return String.fromCharCode(parseInt(p1, 16));
    }));
}

/**
 * Decoding base64 -> UTF8
 * Everytime we need to decode the base64 utf8 string generated by b64EncodeUtf8
 *  first we atob base64 into utf8
 *      '4pyT' ->  0xE2 0x9C 0x93  which is literally: 'â\x9C\x93'
 *  then manually prefix % for each byte and generates
 *      0xE2 0x9C 0x93  which is literally: 'â\x9C\x93' -> '%e2%9c%93'
 *  and use decodeURIComponent to decode this utf8 bytes into a string
 *      '%e2%9c%93' -> '✓'
 * @param str
 */
export function b64DecodeUtf8(str: string) {
    return decodeURIComponent(Array.prototype.map.call(atob(str), function(c: string) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
}

export function serializeCircularReference(circularReference: unknown): number[] {
    return Array.from(Uint8Array.from(b64EncodeUtf8(stringifyCircularReference(circularReference)), (c) => c.charCodeAt(0)));
}

/**
 * Simple string hash (djb2) returning a short base-36 key.
 */
export function shortHash(input: string): string {
    let hash = 5381;
    for (let i = 0; i < input.length; i++) {
        hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(36);
}


/**
 * return a string in the format of "a, b, c, or d"
 * return a string in the format of "a, b, c, and d"
 */

export function formatListWithConjunction(list: string[], conjunction: string = stringResources.Or): string {
    if (list.length === 0) {
        return '';
    }
    if (list.length === 1) {
        return list[0];
    }
    const lastItem = list[list.length - 1];
    return `${list.slice(0, -1).join(', ')} ${conjunction} ${lastItem}`;
}

/**
 * currently the function supports only positive duration
 * @param duration
 * @returns formatted string
 */
export function formatDurationString(duration: number): string {
    if (duration < 1000) {
        return stringResources.LessThan1s;
    }
    duration = Math.trunc((duration + 999) / 1000);
    const hourInSeconds = 60 * 60;
    const dayInSeconds = hourInSeconds * 24;
    const days = Math.floor((duration) / dayInSeconds);
    const hours = Math.floor((duration % dayInSeconds) / hourInSeconds);
    const minutes = Math.floor((duration % hourInSeconds) / 60);
    const seconds = Math.floor(duration % 60);

    if (days) {
        return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    }
    else if (hours) {
        return `${hours}h ${minutes}m ${seconds}s`;
    }
    else if (minutes) {
        return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;

}
